import { createSlice } from '@reduxjs/toolkit';
import { getTransactionsAction, syncTransactionsAction, batchEditTransactionsAction, updateTransactionLineItemAction } from 'redux/action/transactionAction';
import { toastText } from 'utils/utils';

const initialState: any = {
    transactions: [],
    pagination: {
        currentPage: 1,
        totalPages: 0,
        totalRecords: 0,
        limit: 20,
    },
    grandTotal: 0,
    isLoading: false,
    isLoadingMore: false,
    hasMore: true,
    error: null,
    fistTimeFetchLoading: true,
    sortConfig: {
        sortBy: 'txnDate',
        sortOrder: 'desc',
    },
    filters: {
        fromDate: null,
        toDate: null,
        accountIds: [],
        customerIds: [],
        classIds: [],
        locationIds: [],
    },
    batchEdit: {
        isLoading: false,
        error: null,
    },
    syncLoading: false,
    // Add current tab state
    currentTab: '1',
};

const TransactionSlice = createSlice({
    name: 'transactions',
    initialState,
    reducers: {
        clearRedux: () => {
            return {
                ...initialState,
            };
        },
        updateLoadingHandler: (state) => {
            state.isLoading = false;
        },
        updateSortConfig: (state, action) => {
            state.sortConfig = action.payload;
        },
        updateFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {
                fromDate: null,
                toDate: null,
                accountIds: [],
                customerIds: [],
                classIds: [],
                locationIds: [],
            };
        },
        resetTransactions: (state) => {
            state.transactions = [];
            state.pagination.currentPage = 1;
            state.hasMore = true;
        },
        updateCurrentTab: (state, action) => {
            state.currentTab = action.payload;
        },
        setSyncLoading: (state, action) => {
            state.syncLoading = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Get Transactions
        builder.addCase(getTransactionsAction.pending, (state, action) => {
            const page = action.meta.arg.page || 1;
            if (page === 1) {
                // First page - initial load
                state.fistTimeFetchLoading = true;
                state.isLoading = true;
            } else {
                // Subsequent pages - infinite scroll
                state.isLoadingMore = true;
            }
            state.error = null;
        });
        builder.addCase(getTransactionsAction.fulfilled, (state, action) => {
            if (!action.payload) return;
            const page = action.meta.arg.page || 1;
            if (page === 1) {
                // First page - replace all transactions
                state.transactions = action.payload.transactions;
                state.grandTotal = action.payload.grandTotal || 0;
                state.fistTimeFetchLoading = false;
                state.isLoading = false;
            } else {
                // Subsequent pages - append to existing transactions
                state.transactions = [...state.transactions, ...action.payload.transactions];
                state.isLoadingMore = false;
            }
            state.pagination = action.payload.pagination;
            state.hasMore = action.payload.pagination.currentPage < action.payload.pagination.totalPages;
        });
        builder.addCase(getTransactionsAction.rejected, (state, action: any) => {
            state.error = action.payload;
            state.fistTimeFetchLoading = false;
            state.isLoading = false;
            state.isLoadingMore = false;
            if (!action.payload?.cancelled) {
                toastText(action.payload?.message || 'Failed to fetch transactions', 'error');
            }
        });

        // Sync Transactions
        builder.addCase(syncTransactionsAction.pending, (state) => {
            state.syncLoading = true;
            state.error = null;
        });
        builder.addCase(syncTransactionsAction.fulfilled, (state) => {
            state.syncLoading = false;
            toastText('Sync process started successfully', 'success', 10000);
        });
        builder.addCase(syncTransactionsAction.rejected, (state, action: any) => {
            state.syncLoading = false;
            state.error = action.payload;
            toastText(action.payload?.message || 'Failed to start sync process', 'error');
        });

        // Batch Edit Transactions
        builder.addCase(batchEditTransactionsAction.pending, (state) => {
            state.batchEdit.isLoading = true;
            state.batchEdit.error = null;
        });
        builder.addCase(batchEditTransactionsAction.fulfilled, (state, action) => {
            state.batchEdit.isLoading = false;
            toastText(action.payload?.message || 'Batch edit process started successfully', 'success', 10000);
        });
        builder.addCase(batchEditTransactionsAction.rejected, (state, action: any) => {
            state.batchEdit.isLoading = false;
            state.batchEdit.error = action.payload;
            toastText(action.payload?.message || 'Failed to start batch edit process', 'error');
        });

        // Update Single Transaction Line Item
        builder.addCase(updateTransactionLineItemAction.pending, (state) => {
            state.error = null;
        });
        builder.addCase(updateTransactionLineItemAction.fulfilled, (state, action) => {
            const { txnId, lineId, entityType, accountRef, customerRef, classRef, locationRef, accountIds, customerIds, classIds, locationIds } = action.meta.arg;
            
            // Helper function to check if a transaction matches the current filters
            const matchesFilters = (txn: any): boolean => {
                // Check account filter
                if (accountIds && accountIds.length > 0 && !accountIds.includes(txn.accountId)) {
                    return false;
                }
                
                // Check customer filter (empty/undefined values should match 'blank' in filter)
                if (customerIds && customerIds.length > 0) {
                    const txnCustomerId = txn.customerId === '' || txn.customerId === undefined ? 'blank' : txn.customerId;
                    if (!customerIds.includes(txnCustomerId)) {
                        return false;
                    }
                }
                
                // Check class filter (empty/undefined values should match 'blank' in filter)
                if (classIds && classIds.length > 0) {
                    const txnClassId = txn.classId === '' || txn.classId === undefined ? 'blank' : txn.classId;
                    if (!classIds.includes(txnClassId)) {
                        return false;
                    }
                }
                
                // Check location filter (empty/undefined values should match 'blank' in filter)
                if (locationIds && locationIds.length > 0) {
                    const txnLocationId = txn.locationId === '' || txn.locationId === undefined ? 'blank' : txn.locationId;
                    if (!locationIds.includes(txnLocationId)) {
                        return false;
                    }
                }
                
                return true;
            };
            
            // For location updates on non-JournalEntry entities, location is transaction-wise
            // For JournalEntry, location is line-item specific
            const isLocationTxnWise = locationRef !== undefined && entityType !== 'JournalEntry';
            
            // For customer updates: invoice, creditmemo, sales receipt, refund receipt are transaction-wise (like deposit)
            const customerTxnWiseTypes = ['Invoice', 'CreditMemo', 'SalesReceipt', 'RefundReceipt'];
            const isCustomerTxnWise = customerRef !== undefined && customerTxnWiseTypes.some(type => type.toLowerCase() === entityType.toLowerCase());
            
            // Check if account update should be disabled (when linebasedon is 'Item')
            // Find any transaction with the same txnId to check linebasedon (it's a transaction-level field)
            const transactionForLinebasedon = state.transactions.find(
                (txn: any) => 
                    txn.txnId === txnId && 
                    txn.entityType === entityType
            );
            const isAccountDisabled = transactionForLinebasedon && (transactionForLinebasedon as any).linebasedon === 'Item';
            
            // Find the specific line item being updated (for account, customer, class updates)
            const transactionIndex = state.transactions.findIndex(
                (txn: any) => 
                    txn.txnId === txnId && 
                    txn.qboLineId === lineId && 
                    txn.entityType === entityType
            );

            if (isLocationTxnWise || isCustomerTxnWise) {
                // Update location/customer for all line items of this transaction
                const updatedTransactions: any[] = [];
                const transactionsToCheck: any[] = [];
                
                state.transactions.forEach((txn: any) => {
                    if (txn.txnId === txnId && txn.entityType === entityType) {
                        const updatedTxn = { ...txn };
                        
                        // Update location if location is transaction-wise
                        if (isLocationTxnWise && locationRef) {
                            updatedTxn.locationId = locationRef.value || undefined;
                            // Preserve empty string for blank selections, don't convert to undefined
                            updatedTxn.locationName = locationRef.value === '' ? '' : (locationRef.name || undefined);
                        }
                        
                        // Update customer if customer is transaction-wise (invoice, creditmemo, sales receipt, refund receipt, deposit)
                        if (isCustomerTxnWise && customerRef !== undefined) {
                            updatedTxn.customerId = customerRef.value || undefined;
                            // Preserve empty string for blank selections, don't convert to undefined
                            updatedTxn.customerName = customerRef.value === '' ? '' : (customerRef.name || undefined);
                        }
                        
                        // Also update account, customer (if not transaction-wise), class for the specific line item if they changed
                        if (txn.qboLineId === lineId) {
                            // Only update account if not disabled (linebasedon !== 'Item')
                            if (accountRef && !isAccountDisabled) {
                                updatedTxn.accountId = accountRef.value;
                                updatedTxn.accountName = accountRef.name;
                            }
                            // Update customer only if not transaction-wise (for purchase, bill, vendorcredit)
                            if (!isCustomerTxnWise && customerRef !== undefined) {
                                updatedTxn.customerId = customerRef.value || undefined;
                                // Preserve empty string for blank selections, don't convert to undefined
                                updatedTxn.customerName = customerRef.value === '' ? '' : (customerRef.name || undefined);
                            }
                            if (classRef !== undefined) {
                                updatedTxn.classId = classRef.value || undefined;
                                // Preserve empty string for blank selections, don't convert to undefined
                                updatedTxn.className = classRef.value === '' ? '' : (classRef.name || undefined);
                            }
                        }
                        
                        transactionsToCheck.push(updatedTxn);
                    } else {
                        updatedTransactions.push(txn);
                    }
                });
                
                // Filter out transactions that don't match the current filters
                transactionsToCheck.forEach((txn: any) => {
                    if (matchesFilters(txn)) {
                        updatedTransactions.push(txn);
                    }
                });
                
                state.transactions = updatedTransactions;
            } else if (transactionIndex !== -1) {
                // For line-item specific updates (account, customer, class, or JournalEntry location)
                // Update only the specific transaction line item
                const updatedTransaction = { ...state.transactions[transactionIndex] };
                
                // Only update account if not disabled (linebasedon !== 'Item')
                if (accountRef && !isAccountDisabled) {
                    updatedTransaction.accountId = accountRef.value;
                    updatedTransaction.accountName = accountRef.name;
                }
                if (customerRef !== undefined) {
                    updatedTransaction.customerId = customerRef.value || undefined;
                    // Preserve empty string for blank selections, don't convert to undefined
                    updatedTransaction.customerName = customerRef.value === '' ? '' : (customerRef.name || undefined);
                    
                    // For deposits, when a customer is set (not blank), clear the vendor for this line item only
                    // Vendor and customer are mutually exclusive in deposits
                    if (entityType === 'Deposit' && customerRef.value && customerRef.value !== '') {
                        updatedTransaction.vendorId = '';
                        updatedTransaction.vendorName = '';
                    }
                }
                if (classRef !== undefined) {
                    updatedTransaction.classId = classRef.value || undefined;
                    // Preserve empty string for blank selections, don't convert to undefined
                    updatedTransaction.className = classRef.value === '' ? '' : (classRef.name || undefined);
                }
                if (locationRef !== undefined) {
                    // For JournalEntry, location is line-item specific
                    updatedTransaction.locationId = locationRef.value || undefined;
                    // Preserve empty string for blank selections, don't convert to undefined
                    updatedTransaction.locationName = locationRef.value === '' ? '' : (locationRef.name || undefined);
                }

                // Check if the updated transaction matches the current filters
                if (matchesFilters(updatedTransaction)) {
                    // Update the transaction in the array
                    state.transactions[transactionIndex] = updatedTransaction;
                } else {
                    // Remove the transaction if it doesn't match the filters
                    state.transactions.splice(transactionIndex, 1);
                }
            }
        });
        builder.addCase(updateTransactionLineItemAction.rejected, (state, action: any) => {
            state.error = action.payload;
        });
    },
});

export default TransactionSlice;
export const {
    clearRedux,
    updateLoadingHandler,
    updateSortConfig,
    updateFilters,
    clearFilters,
    resetTransactions,
    updateCurrentTab,
    setSyncLoading
} = TransactionSlice.actions;
